/**
 * Live chat dictation for plans with `voice_transcription` (MAR-1423): the microphone, as 16 kHz PCM,
 * over a WebSocket to `/api/dictation/live`, which relays it through the LLM gateway to Gemini Live
 * (`gemini-3.5-transcribe-live`). Interim text arrives while the user speaks, final text on pauses.
 *
 * `start` resolves `false` when the live path cannot be used — no microphone, no token, the server
 * answering `unavailable` (plan, gateway, quota, Google), or no answer in time. The composer then
 * falls back to the browser's own `SpeechRecognition` (`ChatInput.vue`).
 *
 * Turns: nothing is sent until the user speaks, and about a second of silence after speech sends
 * `{"type":"pause"}`, which closes the turn so Gemini finalises it (`TurnTracker`, `utils/dictation/pcm.ts`).
 *
 * Cost guards (Alex, 2026-10-28): a dictation stops itself after `SILENCE_STOP_MS` of silence and
 * after `DICTATION_MAX_MS`, and never reconnects. The gateway enforces the 10 minutes as well.
 */
import { getCurrentInstance, onScopeDispose } from 'vue'
import {
  DICTATION_MAX_MS,
  PcmChunker,
  SilenceWatch,
  TurnTracker,
  chunkLevel,
  downsampleToInt16,
} from '~/utils/dictation/pcm'

export interface LiveDictationHandlers {
  /** Every finished utterance so far, and the utterance in progress. */
  onText(finals: string[], interim: string): void
  /** The dictation is over: stopped, silent, out of time, or the connection ended. */
  onEnd(): void
}

/** How long the server has to say `ready` before the composer falls back. */
const READY_TIMEOUT_MS = 8_000
/** After `stop`, how long the last transcript may take. */
const DRAIN_MS = 2_000
/** Frames kept while waiting for `ready` (~10 s of audio). */
const MAX_PENDING_CHUNKS = 100

/** `public/dictation/pcm-tap-worklet.js`: posts each block of microphone samples to this thread. */
const WORKLET_URL = '/dictation/pcm-tap-worklet.js'

export function useLiveDictation() {
  let teardown: (() => void) | null = null
  let finish: (() => void) | null = null

  function supported(): boolean {
    return typeof window !== 'undefined'
      && typeof WebSocket !== 'undefined'
      && typeof AudioWorkletNode !== 'undefined'
      && Boolean(navigator.mediaDevices?.getUserMedia)
  }

  async function start(handlers: LiveDictationHandlers): Promise<boolean> {
    if (!supported()) return false
    const { getToken } = useAuth()
    const token = await getToken()
    if (!token) return false

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } })
    } catch {
      return false
    }

    const context = new AudioContext()
    const cleanupAudio = () => {
      stream.getTracks().forEach(track => track.stop())
      void context.close().catch(() => {})
    }
    let node: AudioWorkletNode
    try {
      await context.audioWorklet.addModule(WORKLET_URL)
      node = new AudioWorkletNode(context, 'marcus-pcm-tap')
      context.createMediaStreamSource(stream).connect(node)
    } catch {
      cleanupAudio()
      return false
    }

    const socket = new WebSocket(`${location.origin.replace(/^http/, 'ws')}/api/dictation/live`)
    socket.binaryType = 'arraybuffer'

    const chunker = new PcmChunker()
    const silence = new SilenceWatch(Date.now())
    const turns = new TurnTracker()
    const pending: Array<Uint8Array | string> = []
    const finals: string[] = []
    let interim = ''
    let ready = false
    let ended = false
    let capTimer: ReturnType<typeof setTimeout> | null = null
    let drainTimer: ReturnType<typeof setTimeout> | null = null

    const end = () => {
      if (ended) return
      ended = true
      if (capTimer) clearTimeout(capTimer)
      if (drainTimer) clearTimeout(drainTimer)
      node.port.onmessage = null
      cleanupAudio()
      try { socket.close() } catch { /* already closed */ }
      teardown = null
      finish = null
      if (ready) {
        // Whatever was still interim when the dictation ended stays in the composer.
        if (interim) handlers.onText([...finals, interim], '')
        handlers.onEnd()
      }
    }

    /** Stop listening, let the last transcript arrive, then close. */
    const stopListening = () => {
      if (ended) return
      node.port.onmessage = null
      stream.getTracks().forEach(track => track.stop())
      if (ready && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'end' }))
        drainTimer = setTimeout(end, DRAIN_MS)
      } else {
        end()
      }
    }

    const sendFrame = (frame: Uint8Array | string) => {
      if (ready) socket.send(frame)
      else if (pending.length < MAX_PENDING_CHUNKS) pending.push(frame)
    }

    node.port.onmessage = (event: MessageEvent<Float32Array>) => {
      const now = Date.now()
      for (const chunk of chunker.push(downsampleToInt16(event.data, context.sampleRate))) {
        const level = chunkLevel(chunk)
        const { send, pause } = turns.push(chunk, level, now)
        for (const frame of send) sendFrame(frame)
        if (pause) sendFrame(JSON.stringify({ type: 'pause' }))
        if (ready && silence.observe(level, now)) {
          stopListening()
          return
        }
      }
    }

    const opened = await new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => resolve(false), READY_TIMEOUT_MS)
      socket.onopen = () => socket.send(JSON.stringify({ type: 'start', token }))
      socket.onmessage = (event: MessageEvent) => {
        if (typeof event.data !== 'string') return
        let message: { type?: string, text?: string }
        try { message = JSON.parse(event.data) } catch { return }
        if (!ready) {
          clearTimeout(timer)
          resolve(message.type === 'ready')
          return
        }
        if (message.type === 'interim') {
          interim = message.text ?? ''
          silence.heard(Date.now())
          handlers.onText([...finals], interim)
        } else if (message.type === 'final') {
          if (message.text) finals.push(message.text)
          interim = ''
          silence.heard(Date.now())
          handlers.onText([...finals], interim)
        } else if (message.type === 'closed') {
          end()
        }
      }
      socket.onclose = () => {
        clearTimeout(timer)
        if (ready) end()
        else resolve(false)
      }
      socket.onerror = () => { /* the close that follows decides */ }
    })

    if (!opened) {
      ended = true
      node.port.onmessage = null
      cleanupAudio()
      try { socket.close() } catch { /* already closed */ }
      return false
    }

    ready = true
    silence.heard(Date.now())
    for (const chunk of pending.splice(0)) socket.send(chunk)
    capTimer = setTimeout(stopListening, DICTATION_MAX_MS)
    teardown = end
    finish = stopListening
    return true
  }

  /** The user's stop: the last words still arrive before the dictation ends. */
  function stop(): void {
    finish?.()
  }

  if (getCurrentInstance()) onScopeDispose(() => teardown?.())

  return { start, stop, supported }
}
